import { theme } from '@Guidelines/Theme/theme';
import ProviderCard from '@UI/Pages/Administration/AiEngines/Sections/ProvidersSection/ProviderCard';
import { openProviderSidebar } from '@UI/Pages/Administration/AiEngines/Signals/providerSidebarSignal';
import { css } from '@emotion/react';
import { Cpu, Plus } from 'lucide-react';
import * as React from 'react';
import { Button } from '@verclam/ui-kit';
import type AiProvidersListView from '@UI/Store/AiConfiguration/Views/AiProvidersListView';
import EmptyState from '@Guidelines/Component/EmptyState';
import WithPermission from '@UI/Providers/PermissionProvider/WithPermission';
import { PermissionEnum } from '@Application/Security/Roles';

const sectionStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1.25rem;
`;

const headerRowStyle = css`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 1rem;
	flex-wrap: wrap;
`;

const eyebrowStyle = css`
	${theme.typography.eyebrow}
	margin: 0;
	text-transform: uppercase;
	color: ${theme.colors.inkMuted};
`;

const cardsGridStyle = css`
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(min(22rem, 100%), 1fr));
	gap: 1.25rem;
`;

type Props = {
	providers: AiProvidersListView;
};

/**
 * Self-contained block: the read gate is the tab's, and loading, error and the account-wide empty
 * states are the mounting screen's job (`AiEnginesContent.tsx`), never repeated here. Reads `aiConfigurationStore.providers` already loaded
 * by that screen; never fetches on its own.
 */
const ProvidersSection: React.FC<Props> = ({ providers }) => {
	const handleCreateClick = React.useCallback(() => {
		openProviderSidebar(null);
	}, []);

	if (providers.list.length === 0) {
		return (
			<EmptyState
				title="Aucun fournisseur d’IA n’est déclaré."
				icon={<Cpu size={28} />}
				action={
					<WithPermission permission={PermissionEnum.AI_PROVIDER_WRITE}>
						<Button
							type="button"
							label="Ajouter un fournisseur"
							icon={<Plus size={16} />}
							onClick={handleCreateClick}
						/>
					</WithPermission>
				}
			/>
		);
	}

	return (
		<section css={sectionStyle}>
			<div css={headerRowStyle}>
				<p css={eyebrowStyle}>
					FOURNISSEURS D’IA · {providers.list.length} DÉCLARÉ{providers.list.length > 1 ? 'S' : ''}
				</p>
				<WithPermission permission={PermissionEnum.AI_PROVIDER_WRITE}>
					<Button
						type="button"
						label="Ajouter un fournisseur"
						icon={<Plus size={16} />}
						onClick={handleCreateClick}
					/>
				</WithPermission>
			</div>
			<div css={cardsGridStyle}>
				{providers.list.map((provider) => (
					<ProviderCard key={provider.id} provider={provider} />
				))}
			</div>
		</section>
	);
};

export default ProvidersSection;
